/**
 * Text Display Settings - Default values and localStorage persistence
 *
 * Default values are based on client specifications for optimal readability,
 * particularly for users with dyslexia.
 */

import {
  isSpeechLanguage,
  SPEECH_LANGUAGE_AUTO,
  type SpeechLanguageSetting,
} from './speech-language';
import { clampSpeechRate, getDefaultSpeechRate } from './voice-speech';

const STORAGE_KEY = 'dyslexa-text-display-settings';

/**
 * Settings interface for text display configuration
 */
export interface TextDisplaySettings {
  fontFamily: string;
  fontSize: number;
  lineSpacing: number;
  textColor: string;
  backgroundColor: string;
  newSentenceNewLine: boolean;
  ttsVoiceURI: string;
  /** Speech speed, or 0 to follow the device default. See clampSpeechRate. */
  ttsRate: number;
  /** Reading language, or 'auto' to detect it from the text. */
  ttsLanguage: SpeechLanguageSetting;
  /**
   * @deprecated Nothing sets this any more. It is read once, on load, to migrate
   * storage written before COLOR_PRESETS, and is always written back as 'off'.
   * It stays in the shape because the settings are one object under one key, so
   * dropping a field rewrites that object without it - and ttsRate, the reader's
   * saved speed, is in the same object.
   */
  highContrast: HighContrastMode;
}

/**
 * The named high-contrast modes the colour presets replaced, and 'off'.
 *
 * Legacy: nothing sets one any more, and the only code that reads one is the
 * migration in normalizeSettings. The argument that made this a mode rather than
 * more colours was sound, and the presets satisfy it better rather than
 * abandoning it. Two sliders moving independently over six values each put 36
 * combinations within reach and some of them were unreadable - light grey
 * #EDEDED on light yellow #FFF3B0 was two swipes from the default - so a named
 * pair that set both halves at once, and could not be desynchronised, was the
 * only safe way to offer pure black and pure white. COLOR_PRESETS applies that
 * rule to the whole control instead of to one escape hatch inside it: a tile
 * sets both halves in a single action, no loose halves are left to combine, and
 * the reader picks from nine pairs that were each chosen as a pair rather than
 * from 36 that were not. Black on white is now simply preset 2; white on black
 * has no exact successor, which is the one thing the migration cannot do
 * losslessly.
 */
export type HighContrastMode = 'off' | 'blackOnWhite' | 'whiteOnBlack';

/**
 * One tile in the colour grid: a font colour and the background it belongs on.
 */
export interface ColorPreset {
  /**
   * The tile's accessible name, in Polish, and naming both colours. A screen
   * reader user cannot see the swatch, so "zestaw 7" would tell them nothing.
   */
  label: string;
  textColor: string;
  backgroundColor: string;
}

/**
 * The nine colour pairs the drawer offers, in the order they are laid out -
 * three columns, three rows, read left to right.
 *
 * This is the whole colour palette now; the two sliders, with their independent
 * lists of font and background colours, are gone. Five of the nine are pairs the
 * sliders already offered at the same index, so most returning readers keep
 * exactly what they had; a sixth is one of those with its paper tint nudged; one
 * is the old blackOnWhite mode; two are new.
 *
 * Order matters twice over: it is the reading order of the grid and of arrow-key
 * movement, and the migration tables below map onto these positions. The pairs
 * live here rather than in the screen because the loader has to validate against
 * them, and because a pair that only exists next to its own JSX is a pair the
 * next person will quietly duplicate.
 */
export const COLOR_PRESETS: readonly ColorPreset[] = [
  { label: 'Jasnoszary tekst na ciemnym tle', textColor: '#EDEDED', backgroundColor: '#1B1B1B' },
  { label: 'Czarny tekst na białym tle', textColor: '#000000', backgroundColor: '#FFFFFF' },
  { label: 'Czarny tekst na kremowym tle', textColor: '#1A1A1A', backgroundColor: '#FAF3E0' },
  { label: 'Granatowy tekst na jasnożółtym tle', textColor: '#0B1F3A', backgroundColor: '#FFF3B0' },
  { label: 'Ciemnoszary tekst na jasnoszarym tle', textColor: '#222222', backgroundColor: '#F2F2F2' },
  { label: 'Brązowy tekst na brzoskwiniowym tle', textColor: '#2B1B0E', backgroundColor: '#FBE3D6' },
  { label: 'Morski tekst na jasnoniebieskim tle', textColor: '#0F2E3A', backgroundColor: '#EAF3FF' },
  { label: 'Ciemnoszary tekst na różowym tle', textColor: '#262626', backgroundColor: '#F2D9EE' },
  { label: 'Jasnoszary tekst na granatowym tle', textColor: '#F2F2F2', backgroundColor: '#0B1F3A' },
];

/**
 * The pair the app opens with, and where anything unrecognised lands.
 *
 * Preset 3, which is the slider default unchanged - near-black on warm cream. A
 * reader who never touched the colours has to see no difference at all.
 */
export const DEFAULT_COLOR_PRESET_INDEX = 2;

export const LINE_SPACINGS = [100, 120, 150, 180, 200, 220];

/**
 * Fixed CSS properties that are not user-configurable
 * These are applied directly to the text content for optimal readability
 */
export const FIXED_TEXT_STYLES = {
  letterSpacing: '0.04em',
  wordSpacing: '0.14em', // 3.5x letter spacing as per client spec
  textAlign: 'left' as const,
} as const;

/**
 * Default settings based on current UI options:
 * - Font: Verdana
 * - Size: 16px
 * - Line height: 1.5 (150%)
 * - Letter spacing: 0.04em (fixed, not in this object)
 * - Word spacing: 0.14em / 3.5x letter spacing (fixed, not in this object)
 * - Text alignment: left, no justification (fixed)
 * - Colours: preset 3, #1A1A1A on #FAF3E0
 */
export const DEFAULT_SETTINGS: TextDisplaySettings = {
  fontFamily: 'Verdana, sans-serif',
  fontSize: 16,
  lineSpacing: 150,
  textColor: COLOR_PRESETS[DEFAULT_COLOR_PRESET_INDEX].textColor,
  backgroundColor: COLOR_PRESETS[DEFAULT_COLOR_PRESET_INDEX].backgroundColor,
  newSentenceNewLine: false,
  ttsVoiceURI: 'auto',
  // 0 rather than a number: the sensible starting speed differs per device, and
  // baking one in here would hand an iPhone the desktop rate on first run.
  ttsRate: 0,
  ttsLanguage: SPEECH_LANGUAGE_AUTO,
  highContrast: 'off',
};

/**
 * Where a saved high-contrast mode lands. Migration data, nothing else.
 *
 * The mode beats the stored pair, and that is the subtle half of this migration:
 * a mode overrode the displayed colours without overwriting the two slider values
 * underneath it, so a reader on blackOnWhite still has some unrelated pair in
 * storage. The mode is what they were actually looking at, so the mode wins.
 */
const LEGACY_MODE_PRESETS: Record<Exclude<HighContrastMode, 'off'>, number> = {
  // Preset 2 is that mode exactly - black on white, nothing lost.
  blackOnWhite: 1,
  /*
   * A known contrast regression, and still open with the client.
   *
   * There is no pure white on pure black among the nine, and preset 1 (#EDEDED
   * on #1B1B1B) is the nearest - but preset 1 is also what the sliders already
   * offered as dark mode, so a reader who turned this mode on chose it over that
   * exact pair, deliberately, for more contrast. Sending them to preset 1 hands
   * them the thing they rejected. If the answer comes back as a tenth preset,
   * adding it is a row in COLOR_PRESETS and a number here.
   */
  whiteOnBlack: 0,
};

/**
 * Where each old slider background lands. Migration data, nothing else.
 *
 * Keyed on the background alone because the two sliders moved independently, so
 * the font colour stored beside it can be any of the six - including one that
 * never belonged there. The background is the half that says which pair the
 * reader was closest to.
 */
const LEGACY_BACKGROUND_PRESETS: Record<string, number | undefined> = {
  '#FAF3E0': 2, // warm cream - preset 3, the default, unchanged
  '#FFF3B0': 3, // light yellow - unchanged
  '#F2F2F2': 4, // light gray - unchanged
  /*
   * Warm beige, the one old background with no exact successor: preset 6 shifts
   * the paper to #FBE3D6. It keeps the font colour #2B1B0E, so the reader's text
   * does not change at all and only the tint moves, slightly.
   */
  '#F5E6D3': 5,
  '#EAF3FF': 6, // light blue - unchanged
  '#1B1B1B': 0, // dark mode - unchanged
};

/** The preset holding exactly this pair, or -1. */
function exactColorPresetIndex(textColor: string, backgroundColor: string): number {
  return COLOR_PRESETS.findIndex(
    (preset) => preset.textColor === textColor && preset.backgroundColor === backgroundColor,
  );
}

/**
 * The preset a stored pair means, migrating it if it was written before the tiles.
 *
 * Three steps, in this order: a high-contrast mode wins outright, because it is
 * what overrode the screen; then an exact pair, which is everyone who has already
 * picked a tile; then the old slider background, through the table above.
 * Anything else is storage this app never wrote, and it gets the default.
 */
function resolveColorPreset(
  settings: Pick<TextDisplaySettings, 'textColor' | 'backgroundColor' | 'highContrast'>,
): ColorPreset {
  if (isHighContrastMode(settings.highContrast) && settings.highContrast !== 'off') {
    return COLOR_PRESETS[LEGACY_MODE_PRESETS[settings.highContrast]];
  }

  const exact = exactColorPresetIndex(settings.textColor, settings.backgroundColor);
  if (exact !== -1) {
    return COLOR_PRESETS[exact];
  }

  const legacy = LEGACY_BACKGROUND_PRESETS[settings.backgroundColor];
  return COLOR_PRESETS[legacy ?? DEFAULT_COLOR_PRESET_INDEX];
}

/**
 * Which tile is selected - never -1.
 *
 * The same last-resort guard as paletteIndex, for the same reason: with the
 * loader migrating, this cannot miss, and if it ever did, the default tile beats
 * a grid with nothing checked and therefore no tab stop in it.
 */
export function colorPresetIndex(
  pair: Pick<TextDisplaySettings, 'textColor' | 'backgroundColor'>,
): number {
  const index = exactColorPresetIndex(pair.textColor, pair.backgroundColor);
  return index === -1 ? DEFAULT_COLOR_PRESET_INDEX : index;
}

/**
 * Snaps the saved settings back onto something the UI can render, and migrates
 * the colour pair of every reader who was here before the tiles.
 *
 * lineSpacing still feeds a slider whose thumb position and input value are both
 * computed with palette.indexOf(saved). A value that is not in its palette gives
 * -1, which renders the thumb at a negative offset and puts the range input below
 * its own min - and because nothing validated these on load, a single bad value
 * in localStorage survived every reload rather than being corrected once.
 *
 * The colours no longer feed a slider, but they have a harder job. Every
 * returning reader has a pair in storage that the sliders wrote, and the grid can
 * only show one of nine; dropping the unrecognised ones on the default would
 * silently reset a choice somebody made on purpose, so resolveColorPreset maps
 * them instead. fontFamily and fontSize are still validated in the screen, where
 * their palettes live.
 *
 * Called from loadSettings, so storage has exactly one door and a returning
 * reader cannot come back to a broken control.
 */
export function normalizeSettings(settings: TextDisplaySettings): TextDisplaySettings {
  const preset = resolveColorPreset(settings);

  return {
    ...settings,
    lineSpacing: LINE_SPACINGS.includes(settings.lineSpacing)
      ? settings.lineSpacing
      : DEFAULT_SETTINGS.lineSpacing,
    textColor: preset.textColor,
    backgroundColor: preset.backgroundColor,
    // Always off now, and written back so the migration runs once rather than on
    // every load. The pair above is what the mode used to produce.
    highContrast: 'off',
    ttsRate: settings.ttsRate ? clampSpeechRate(settings.ttsRate) : DEFAULT_SETTINGS.ttsRate,
    ttsLanguage:
      isSpeechLanguage(settings.ttsLanguage) || settings.ttsLanguage === SPEECH_LANGUAGE_AUTO
        ? settings.ttsLanguage
        : DEFAULT_SETTINGS.ttsLanguage,
  };
}

/** The speed to actually use: the saved one, or this device's default. */
export function resolveSpeechRate(settings: Pick<TextDisplaySettings, 'ttsRate'>): number {
  return settings.ttsRate ? clampSpeechRate(settings.ttsRate) : getDefaultSpeechRate();
}

export function isHighContrastMode(value: unknown): value is HighContrastMode {
  return value === 'off' || value === 'blackOnWhite' || value === 'whiteOnBlack';
}

/**
 * Slider position for a saved value - never -1.
 *
 * A last-resort guard at the render site rather than a substitute for
 * normalizeSettings: with the loader validating, this cannot fire, and if it
 * ever does, a thumb parked on the first stop beats one pushed off the left of
 * the track with its input below min.
 */
export function paletteIndex<T>(palette: readonly T[], value: T): number {
  const index = palette.indexOf(value);
  return index === -1 ? 0 : index;
}

/**
 * Load settings from localStorage
 * Returns default settings if no saved settings exist or if parsing fails
 */
export function loadSettings(): TextDisplaySettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_SETTINGS;
    }

    const parsed = JSON.parse(stored) as Partial<TextDisplaySettings>;

    // Merge with defaults to ensure all properties exist
    // This handles cases where new settings are added in future updates
    return normalizeSettings({
      ...DEFAULT_SETTINGS,
      ...parsed,
    });
  } catch (error) {
    console.warn('Failed to load text display settings from localStorage:', error);
    return DEFAULT_SETTINGS;
  }
}

/**
 * Save settings to localStorage
 */
export function saveSettings(settings: TextDisplaySettings): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save text display settings to localStorage:', error);
  }
}

/**
 * Clear saved settings from localStorage (reset to defaults)
 */
export function clearSettings(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear text display settings from localStorage:', error);
  }
}
